import type { Note } from '@just-us/shared'
/**
 * pages/note-inbox.tsx
 *
 * Received notes inbox.
 * - Unified list — unread notes highlighted with blush pink background + pulse
 * - Locked notes show a live countdown until they unlock
 * - Tap card → /notes/:id
 * - FAB → /notes/compose
 * - Header "Sent" button → /notes/sent
 */
import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import BottomNav from '../components/bottom-nav'
import { LockedCountdown } from '../components/locked-countdown'
import { useNotes } from '../hooks/use-notes'
import { getAuthPayload } from '../store/auth'

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

const timeAgo = (unix: number): string => {
  const diffSec = Math.floor(Date.now() / 1000) - unix
  if (diffSec < 60) return 'just now'
  const diffMin = Math.floor(diffSec / 60)
  if (diffMin < 60) return `${diffMin}m ago`
  const diffHr = Math.floor(diffMin / 60)
  if (diffHr < 24) return `${diffHr}h ago`
  const diffDay = Math.floor(diffHr / 24)
  return `${diffDay}d ago`
}

const MOOD_EMOJI: Record<string, string> = {
  lovey: '💕',
  celebratory: '🎉',
  dreamy: '🌙',
  cozy: '☕',
}

const ENVELOPE_EMOJI: Record<string, string> = {
  plain: '✉️',
  heart: '💌',
  flower: '🌸',
}

// ---------------------------------------------------------------------------
// Note card
// ---------------------------------------------------------------------------

interface NoteCardProps {
  note: Note
  onTap: (id: string) => void
}

const NoteCard = ({ note, onTap }: NoteCardProps) => {
  const isSealed = !note.opened_at
  const isLocked = !!(note.unlock_at && Math.floor(Date.now() / 1000) < note.unlock_at)

  return (
    <button
      type="button"
      onClick={() => onTap(note.id)}
      className={[
        'w-full text-left rounded-[16px] border p-4 transition-all duration-200 active:scale-[0.98] active:shadow-none touch-manipulation',
        isSealed
          ? 'bg-[#FBEAF0] border-[#F4C0D1] animate-pulse-border'
          : 'bg-white border-[#F9E0E8] shadow-[0_2px_12px_rgba(212,83,126,0.08)]',
      ].join(' ')}
    >
      <div className="flex items-start gap-3">
        {/* Envelope icon */}
        <div className="w-[40px] h-[40px] rounded-full bg-[#FBEAF0] border border-[#F4C0D1] flex items-center justify-center text-[20px] shrink-0">
          {ENVELOPE_EMOJI[note.envelope_style] ?? '✉️'}
        </div>

        <div className="flex-1 min-w-0">
          <div className="flex items-center justify-between gap-2 mb-1">
            {/* Mood badge */}
            {note.mood && (
              <span className="font-['DM_Sans'] text-[11px] text-[#B89AA2]">
                {MOOD_EMOJI[note.mood]} {note.mood}
              </span>
            )}
            <span className="font-['DM_Sans'] text-[11px] text-[#B89AA2] ml-auto shrink-0">
              {timeAgo(note.created_at)}
            </span>
          </div>

          {/* Content preview */}
          {isLocked ? (
            <LockedCountdown unlockAt={note.unlock_at!} />
          ) : isSealed ? (
            <p className="font-['DM_Sans'] text-[14px] text-[#7A5A62]">Tap to open</p>
          ) : (
            <p className="font-['Caveat'] text-[16px] text-[#7A5A62] line-clamp-2 leading-[1.5]">
              {note.content ? note.content.slice(0, 80) : note.doodle_url ? '🎨 Doodle' : '…'}
            </p>
          )}
        </div>

        {/* Unread dot */}
        {isSealed && <div className="w-[8px] h-[8px] rounded-full bg-[#D4537E] shrink-0 mt-1" />}
      </div>
    </button>
  )
}

// ---------------------------------------------------------------------------
// Skeleton loader
// ---------------------------------------------------------------------------

const NoteCardSkeleton = () => (
  <div className="w-full bg-white rounded-[16px] border border-[#F9E0E8] p-4 animate-shimmer">
    <div className="flex items-start gap-3">
      <div className="w-[40px] h-[40px] rounded-full bg-[#FBEAF0]" />
      <div className="flex-1 flex flex-col gap-2">
        <div className="h-[12px] rounded-full bg-[#F4C0D1] w-2/3" />
        <div className="h-[14px] rounded-full bg-[#F9E0E8] w-full" />
      </div>
    </div>
  </div>
)

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------

export default function NoteInboxPage() {
  const navigate = useNavigate()
  const { data, isLoading } = useNotes('inbox')
  const payload = getAuthPayload()

  const allNotes = data?.data ?? []

  // inbox only shows notes received (sender !== me), already filtered by API
  // but re-confirm locally just in case
  const receivedNotes = allNotes.filter((n) => n.sender_id !== payload?.user_id)
  const unreadCount = receivedNotes.filter((n) => !n.opened_at).length

  return (
    <div className="min-h-screen bg-[#FAF8F4] flex flex-col max-w-[430px] mx-auto relative">
      {/* Sticky header */}
      <header className="sticky top-0 z-40 bg-[#FAF8F4] pt-[env(safe-area-inset-top)] border-b border-[#F9E0E8]">
        <div className="flex items-center justify-between px-5 h-[56px]">
          <button
            type="button"
            onClick={() => navigate('/home')}
            className="w-[36px] h-[36px] rounded-full bg-[#FBEAF0] flex items-center justify-center active:scale-90 transition-all duration-150 touch-manipulation"
            aria-label="Back"
          >
            <span className="text-[16px]">←</span>
          </button>
          <span className="font-['Playfair_Display'] text-[18px] font-medium text-[#2C1810]">
            Notes
            {unreadCount > 0 && (
              <span className="ml-2 inline-flex items-center justify-center w-[18px] h-[18px] rounded-full bg-[#D4537E] font-['DM_Sans'] text-[10px] font-medium text-white leading-none">
                {unreadCount > 9 ? '9+' : unreadCount}
              </span>
            )}
          </span>
          <button
            type="button"
            onClick={() => navigate('/notes/sent')}
            className="font-['DM_Sans'] text-[14px] font-medium text-[#D4537E] active:opacity-60 transition-opacity duration-100 touch-manipulation"
          >
            Sent
          </button>
        </div>
      </header>

      {/* Content */}
      <main className="flex-1 overflow-y-auto px-5 pt-4 pb-[100px] flex flex-col gap-3">
        {isLoading ? (
          <>
            <NoteCardSkeleton />
            <NoteCardSkeleton />
            <NoteCardSkeleton />
          </>
        ) : receivedNotes.length === 0 ? (
          <div className="flex flex-col items-center justify-center pt-16 gap-3 animate-fade-in">
            <div className="text-[48px] animate-float">✉️</div>
            <p className="font-['Playfair_Display'] text-[18px] font-medium text-[#2C1810]">
              No notes yet
            </p>
            <p className="font-['DM_Sans'] text-[13px] text-[#B89AA2] text-center px-8">
              When your partner sends you a note, it will appear here.
            </p>
          </div>
        ) : (
          <div className="flex flex-col gap-3 animate-fade-in-up">
            {receivedNotes.map((note) => (
              <NoteCard key={note.id} note={note} onTap={(id) => navigate(`/notes/${id}`)} />
            ))}
          </div>
        )}
      </main>

      {/* FAB */}
      <button
        type="button"
        onClick={() => navigate('/notes/compose')}
        className="fixed bottom-[88px] right-5 w-[56px] h-[56px] rounded-full bg-[#D4537E] shadow-[0_8px_24px_rgba(212,83,126,0.35)] flex items-center justify-center text-[24px] active:scale-90 active:bg-[#993556] transition-all duration-150 z-40 touch-manipulation"
        aria-label="Compose note"
      >
        ✏️
      </button>

      <BottomNav />
    </div>
  )
}
